import { getLocale } from '@/i18n';

/**
 * Форматирует дату по одному из предустановленных шаблонов.
 * @param {string|number|Date} dateInput - Дата (ISO-строка, timestamp или объект Date)
 * @param {'datetime'|'time'|'dd-mm-yyyy'|'long'|'date'|'short'|'day-month-short'|'day-month-long'} [mode='datetime'] - Режим форматирования:
 *   - 'datetime'     → "12 авг. 2025, 14:05"
 *   - 'time'         → "14:05"
 *   - 'date'         → "12 авг. 2025"
 *   - 'day-month-short' → "12 авг."
 *   - 'day-month-long' → "12 августа"
 *   - 'dd-mm-yyyy'   → "12-08-2025"
 *   - 'short'        → "20/03/2025"
 *   - 'long'         → "3 ноября 2025 г."
 * @returns {string}
 */
export function formatDate(dateInput, mode = 'datetime') {
  if (!dateInput) return '—';
  const isoDateMatch = typeof dateInput === 'string'
    ? dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    : null;
  const date = isoDateMatch
    ? new Date(Number(isoDateMatch[1]), Number(isoDateMatch[2]) - 1, Number(isoDateMatch[3]))
    : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';
  const locale = getLocale() === 'ru' ? 'ru-RU' : 'en-US';

  if (mode === 'datetime') {
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (mode === 'time') {
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (mode === 'date') {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  if (mode === 'long') {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  if (mode === 'day-month-short') {
    const parts = new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric'
    }).formatToParts(date);
    const day = parts.find((part) => part.type === 'day')?.value || String(date.getDate());
    const monthPart = parts.find((part) => part.type === 'month')?.value || '';
    const monthNormalized = monthPart.replace(/\.$/, '');
    const month = monthNormalized.length > 3 ? `${monthNormalized.slice(0, 3)}.` : monthNormalized;
    return [day, month].filter(Boolean).join(' ');
  }

  if (mode === 'day-month-long') {
    return date.toLocaleDateString(locale, {
      month: 'long',
      day: 'numeric',
    });
  }

  if (mode === 'short') {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  // mode === 'dd-mm-yyyy' (по умолчанию для fallback)
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

// Обратная совместимость со старым кодом:
export function formatDateTime(dateStr) {
  return formatDate(dateStr, 'datetime');
}

export function formatTournamentDate(dateStr) {
  return formatDate(dateStr, 'dd-mm-yyyy');
}

